import { cloneDeep } from "lodash";
import { AitRowGroupData } from "./AitRowGroupData";
import { AitProcessedTableData, AitTableStateBase } from "./interface";
import { repeatHeaders } from "./repeatHeaders";
import { repeatRows } from "./repeatRows";

/**
 * Prepare table data for rendering by expanding repeated headers and rows and
 * applying replacements, external lists/singles and other editor transformation
 * helpers present on the provided `state`.
 *
 * @param state - Table state containing the original table data and editor helpers
 * @returns Processed table data (`AitProcessedTableData`) suitable for rendering
 *          by the table UI (may include `columnRepeats`, transformed `headerData` and `bodyData`).
 */
export const trProcessTable = <T>(state: AitTableStateBase<T>): AitProcessedTableData<T> => {
  const tableData = state.originalTableData;
  let columnRepeats =
    tableData.bodyData.length === 0 || tableData.bodyData[0].rows.length === 0
      ? // istanbul ignore next
        null
      : Array.from(tableData.bodyData[0].rows[0].cells.keys()).map((n) => {
          return { columnIndex: n };
        });
  let processedHeaderData = tableData.headerData;
  if (
    processedHeaderData !== false &&
    (processedHeaderData.rows.length ??
      // istanbul ignore next
      0) > 0
  ) {
    const headerDataUpdate = repeatHeaders(
      processedHeaderData.rows,
      processedHeaderData.replacements ??
        // istanbul ignore next
        [],
      state.defaultCellWidth,
      state.editorProps.getTextFromT,
      state.editorProps.replaceTextInT,
      state.editorProps.blankT,
      state.tlfViewMode,
      tableData.rowHeaderColumns ??
        // istanbul ignore next
        0,
      state.externalLists,
      state.externalSingles,
    );
    processedHeaderData = {
      aitid: processedHeaderData.aitid,
      name: processedHeaderData.name,
      rows: headerDataUpdate.rows,
      comments: processedHeaderData.comments,
      spaceAfter: processedHeaderData.spaceAfter,
      replacements: processedHeaderData.replacements,
      coreTrafficLight: processedHeaderData.coreTrafficLight,
    };
    columnRepeats = headerDataUpdate.columnRepeats;
  }

  // Create processed body
  const processedBodyData: AitRowGroupData<T>[] = tableData.bodyData.map((rg) => {
    return {
      ...rg,
      rows: repeatRows(
        rg.rows,
        state.defaultCellWidth,
        state.editorProps.getTextFromT,
        state.editorProps.replaceTextInT,
        state.editorProps.blankT,
        rg.replacements,
        rg.spaceAfter,
        state.tlfViewMode,
        state.externalLists,
        state.externalSingles,
      ).map((r) => {
        return {
          ...r,
          cells:
            columnRepeats?.map((ci) => r.cells[ci.columnIndex]) ??
            // istanbul ignore next
            r.cells,
        };
      }),
    };
  });
  const processedTableData: AitProcessedTableData<T> = {
    ...cloneDeep(tableData),
    headerData: processedHeaderData,
    bodyData: processedBodyData,
    columnRepeats,
  };

  return processedTableData;
};
